import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  ErrorText,
  Field,
  formatSum,
  Input,
  LocalizedInput,
  Select,
  toSum,
  toTiyin,
  type Localized,
} from '@/components/ui';

interface Size {
  id: string;
  value: string;
  isActive: boolean;
}
interface Brand {
  id: string;
  name: string;
}

interface SizeGroup {
  id: string;
  code: string;
  name: Localized;
  sizes: Size[];
}
interface Color {
  id: string;
  name: Localized;
  hex: string;
  isActive: boolean;
}
interface Variant {
  id: string;
  sizeId: string | null;
  colorId: string | null;
  sku: string;
  quantityTotal: number;
  isActive: boolean;
}
interface Image {
  id: string;
  thumbUrl: string;
  mediumUrl: string;
  isPrimary: boolean;
}
interface Accessory {
  id: string;
  name: Localized;
  price: number;
}
interface Product {
  id: string;
  code: string;
  categoryId: string;
  sizeGroupId: string | null;
  brandId: string | null;
  fabric: Localized | null;
  name: Localized;
  description: Localized | null;
  pricePerDay: number;
  oldPrice: number | null;
  depositAmount: number | null;
  minRentalDays: number;
  maxRentalDays: number;
  bufferDays: number;
  isActive: boolean;
  isNew: boolean;
  isPopular: boolean;
  images: Image[];
  variants: Variant[];
  accessories: Array<{ accessoryId: string; priceOverride: number | null; accessory: Accessory }>;
}
interface CategoryNode {
  id: string;
  level: number;
  name: Localized;
  children: CategoryNode[];
}

export default function ProductEditorPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => (await api.get<Product>(`/admin/products/${id}`)).data,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<CategoryNode[]>('/admin/categories')).data,
  });

  const { data: sizeGroups = [] } = useQuery({
    queryKey: ['size-groups'],
    queryFn: async () => (await api.get<SizeGroup[]>('/admin/size-groups')).data,
  });

  const { data: brands = [] } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => (await api.get<Brand[]>('/admin/brands')).data,
  });

  const { data: colors = [] } = useQuery({
    queryKey: ['colors'],
    queryFn: async () => (await api.get<Color[]>('/admin/colors')).data,
  });

  const { data: accessories = [] } = useQuery({
    queryKey: ['accessories'],
    queryFn: async () => (await api.get<Accessory[]>('/admin/accessories')).data,
  });

  const reload = () => queryClient.invalidateQueries({ queryKey: ['product', id] });
  const fail = (e: unknown) => setError(errorMessage(e));

  if (isLoading || !product) {
    return <p className="text-sm text-neutral-500">Загрузка…</p>;
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <Link to="/products" className="text-sm text-neutral-500 hover:underline">
            ← Товары
          </Link>
          <h1 className="truncate text-xl font-semibold">{product.name.ru}</h1>
          <p className="text-sm text-neutral-500">Артикул {product.code}</p>
        </div>

        <Button variant="danger" onClick={() => setConfirmDelete(true)}>
          Удалить
        </Button>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <ConfirmDialog
        open={confirmDelete}
        title="Удалить товар?"
        description="Вместе с ним исчезнут фотографии и вариации с остатками. Действие необратимо."
        confirmLabel="Удалить"
        tone="danger"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          void api
            .delete(`/admin/products/${id}`)
            .then(() => navigate('/products'))
            .catch(fail);
        }}
      />

      <MainForm
        product={product}
        categories={categories}
        sizeGroups={sizeGroups}
        brands={brands}
        onSaved={reload}
        onError={fail}
      />

      <Gallery product={product} onChanged={reload} onError={fail} />

      <VariantMatrix
        product={product}
        sizeGroups={sizeGroups}
        colors={colors}
        onChanged={reload}
        onError={fail}
      />

      <AccessoryLinks
        product={product}
        accessories={accessories}
        onChanged={reload}
        onError={fail}
      />
    </div>
  );
}

function MainForm({
  product,
  categories,
  sizeGroups,
  brands,
  onSaved,
  onError,
}: {
  product: Product;
  categories: CategoryNode[];
  sizeGroups: SizeGroup[];
  brands: Brand[];
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const [form, setForm] = useState({
    name: product.name,
    description: product.description ?? {},
    categoryId: product.categoryId,
    sizeGroupId: product.sizeGroupId ?? '',
    brandId: product.brandId ?? '',
    fabric: product.fabric ?? {},
    pricePerDay: toSum(product.pricePerDay),
    oldPrice: toSum(product.oldPrice),
    depositAmount: toSum(product.depositAmount),
    minRentalDays: String(product.minRentalDays),
    maxRentalDays: String(product.maxRentalDays),
    bufferDays: String(product.bufferDays),
    isActive: product.isActive,
    isNew: product.isNew,
    isPopular: product.isPopular,
  });

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/admin/products/${product.id}`, {
        name: form.name,
        description: form.description,
        categoryId: form.categoryId,
        sizeGroupId: form.sizeGroupId || undefined,
        brandId: form.brandId || null,
        fabric: form.fabric,
        pricePerDay: toTiyin(form.pricePerDay),
        oldPrice: form.oldPrice ? toTiyin(form.oldPrice) : null,
        depositAmount: form.depositAmount ? toTiyin(form.depositAmount) : null,
        minRentalDays: Number(form.minRentalDays),
        maxRentalDays: Number(form.maxRentalDays),
        bufferDays: Number(form.bufferDays),
        isActive: form.isActive,
        isNew: form.isNew,
        isPopular: form.isPopular,
      }),
    onSuccess: onSaved,
    onError,
  });

  const flat = (nodes: CategoryNode[]): CategoryNode[] =>
    nodes.flatMap((n) => [n, ...flat(n.children)]);

  return (
    <Card
      title="Основное"
      actions={
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      }
    >
      <div className="space-y-4">
        <LocalizedInput
          label="Название"
          value={form.name}
          onChange={(name) => setForm({ ...form, name })}
        />
        <LocalizedInput
          label="Описание"
          multiline
          value={form.description}
          onChange={(description) => setForm({ ...form, description })}
        />
        <LocalizedInput
          label="Состав ткани"
          value={form.fabric}
          onChange={(fabric) => setForm({ ...form, fabric })}
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Категория">
            <Select
              value={form.categoryId}
              onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
            >
              {flat(categories).map((c) => (
                <option key={c.id} value={c.id}>
                  {'— '.repeat(c.level - 1)}
                  {c.name.ru}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Бренд">
            <Select
              value={form.brandId}
              onChange={(e) => setForm({ ...form, brandId: e.target.value })}
            >
              <option value="">Без бренда</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Группа размеров" hint="Без неё товар не имеет размеров">
            <Select
              value={form.sizeGroupId}
              onChange={(e) => setForm({ ...form, sizeGroupId: e.target.value })}
            >
              <option value="">Без размеров</option>
              {sizeGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name.ru}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Цена аренды, сум/сут">
            <Input
              value={form.pricePerDay}
              inputMode="numeric"
              onChange={(e) => setForm({ ...form, pricePerDay: e.target.value })}
            />
          </Field>

          <Field
            label="Старая цена, сум"
            hint="Перечёркивается на витрине; должна быть выше текущей"
          >
            <Input
              value={form.oldPrice}
              inputMode="numeric"
              placeholder="Без скидки"
              onChange={(e) => setForm({ ...form, oldPrice: e.target.value })}
            />
          </Field>

          <Field label="Залог, сум" hint="Пусто — залог не берётся">
            <Input
              value={form.depositAmount}
              inputMode="numeric"
              onChange={(e) => setForm({ ...form, depositAmount: e.target.value })}
            />
          </Field>

          <Field label="Срок аренды, дней">
            <div className="flex items-center gap-2">
              <Input
                value={form.minRentalDays}
                inputMode="numeric"
                onChange={(e) => setForm({ ...form, minRentalDays: e.target.value })}
              />
              <span className="text-neutral-400">–</span>
              <Input
                value={form.maxRentalDays}
                inputMode="numeric"
                onChange={(e) => setForm({ ...form, maxRentalDays: e.target.value })}
              />
            </div>
          </Field>

          <Field
            label="Буфер на чистку, дней"
            hint="Не тарифицируется, блокирует даты после возврата"
          >
            <Input
              value={form.bufferDays}
              inputMode="numeric"
              onChange={(e) => setForm({ ...form, bufferDays: e.target.value })}
            />
          </Field>
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          {(
            [
              ['isActive', 'Показывать в каталоге'],
              ['isNew', 'Новинка'],
              ['isPopular', 'Популярное'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2">
              <Checkbox
                checked={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                className="size-4"
              />
              {label}
            </label>
          ))}
        </div>
      </div>
    </Card>
  );
}

function Gallery({
  product,
  onChanged,
  onError,
}: {
  product: Product;
  onChanged: () => void;
  onError: (e: unknown) => void;
}) {
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);

      // Сначала файл уходит в медиа-модуль, потом его ключ привязывается к товару
      const { data: media } = await api.post<{
        id: string;
        url: string;
        mediumUrl: string;
        thumbUrl: string;
      }>('/admin/media?folder=products', body);

      await api.post(`/admin/products/${product.id}/images`, {
        storageId: media.id,
        url: media.url,
        mediumUrl: media.mediumUrl,
        thumbUrl: media.thumbUrl,
      });
    },
    onSuccess: onChanged,
    onError,
  });

  const remove = useMutation({
    mutationFn: (imageId: string) => api.delete(`/admin/products/${product.id}/images/${imageId}`),
    onSuccess: onChanged,
    onError,
  });

  return (
    <Card title="Фотографии">
      <div className="flex flex-wrap items-center gap-3">
        {product.images.map((image) => (
          <div key={image.id} className="group relative">
            <img src={image.thumbUrl} alt="" className="size-24 rounded-lg object-cover" />
            {image.isPrimary && (
              <span className="absolute left-1 top-1">
                <Badge tone="green">обложка</Badge>
              </span>
            )}
            <button
              onClick={() => remove.mutate(image.id)}
              className="absolute right-1 top-1 rounded bg-black/60 px-1.5 text-xs text-white opacity-0 transition group-hover:opacity-100"
            >
              ✕
            </button>
          </div>
        ))}

        <label className="flex size-24 cursor-pointer items-center justify-center rounded-lg border border-dashed border-neutral-300 text-2xl text-neutral-400 hover:border-neutral-500 dark:border-neutral-700">
          {upload.isPending ? '…' : '+'}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        Изображение сохраняется в WebP в трёх размерах. Первое загруженное становится обложкой.
      </p>
    </Card>
  );
}

/** Таблица «размер × цвет»: в ячейках остатки, пустая ячейка — вариации нет. */
function VariantMatrix({
  product,
  sizeGroups,
  colors,
  onChanged,
  onError,
}: {
  product: Product;
  sizeGroups: SizeGroup[];
  colors: Color[];
  onChanged: () => void;
  onError: (e: unknown) => void;
}) {
  const group = sizeGroups.find((g) => g.id === product.sizeGroupId);
  const sizes = group?.sizes.filter((s) => s.isActive) ?? [];
  const activeColors = colors.filter((c) => c.isActive);

  const key = (sizeId: string | null, colorId: string | null) => `${sizeId ?? ''}|${colorId ?? ''}`;

  const [cells, setCells] = useState<Record<string, string>>({});

  useEffect(() => {
    setCells(
      Object.fromEntries(
        product.variants.map((v) => [key(v.sizeId, v.colorId), String(v.quantityTotal)]),
      ),
    );
  }, [product.variants]);

  const save = useMutation({
    mutationFn: () => {
      const variants = Object.entries(cells)
        .filter(([, value]) => value !== '' && Number(value) >= 0)
        .map(([cellKey, value]) => {
          const [sizeId, colorId] = cellKey.split('|');
          return {
            sizeId: sizeId || undefined,
            colorId: colorId || undefined,
            quantityTotal: Number(value),
          };
        });

      return api.put(`/admin/products/${product.id}/variants`, { variants });
    },
    onSuccess: onChanged,
    onError,
  });

  const rows = sizes.length > 0 ? sizes : [{ id: '', value: 'Без размера', isActive: true }];
  const columns = activeColors.length > 0 ? activeColors : [];
  const total = Object.values(cells).reduce((sum, value) => sum + (Number(value) || 0), 0);

  return (
    <Card
      title="Вариации и остатки"
      actions={
        <div className="flex items-center gap-3">
          <span className="text-sm text-neutral-500">Всего единиц: {total}</span>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить матрицу'}
          </Button>
        </div>
      }
    >
      {columns.length === 0 ? (
        <p className="text-sm text-neutral-500">Сначала заведите цвета в справочнике</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="text-sm">
            <thead>
              <tr>
                <th className="p-2 text-left text-xs uppercase text-neutral-500">Размер \ Цвет</th>
                {columns.map((color) => (
                  <th key={color.id} className="p-2 text-xs font-medium">
                    <span className="flex items-center gap-1.5">
                      <span
                        className="size-3 rounded-full border border-neutral-300"
                        style={{ background: color.hex }}
                      />
                      {color.name.ru}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((size) => (
                <tr key={size.id || 'none'}>
                  <td className="p-2 font-medium">{size.value}</td>
                  {columns.map((color) => {
                    const cellKey = key(size.id || null, color.id);
                    return (
                      <td key={color.id} className="p-1">
                        <Input
                          value={cells[cellKey] ?? ''}
                          inputMode="numeric"
                          placeholder="—"
                          onChange={(e) => setCells({ ...cells, [cellKey]: e.target.value })}
                          className="w-20 text-center"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-neutral-500">
        Пустая ячейка означает, что такой комбинации нет — при сохранении она удаляется.
      </p>
    </Card>
  );
}

function AccessoryLinks({
  product,
  accessories,
  onChanged,
  onError,
}: {
  product: Product;
  accessories: Accessory[];
  onChanged: () => void;
  onError: (e: unknown) => void;
}) {
  const [selected, setSelected] = useState('');
  const linkedIds = new Set(product.accessories.map((a) => a.accessoryId));
  const available = accessories.filter((a) => !linkedIds.has(a.id));

  const link = useMutation({
    mutationFn: () =>
      api.post(`/admin/products/${product.id}/accessories`, { accessoryId: selected }),
    onSuccess: () => {
      setSelected('');
      onChanged();
    },
    onError,
  });

  const unlink = useMutation({
    mutationFn: (accessoryId: string) =>
      api.delete(`/admin/products/${product.id}/accessories/${accessoryId}`),
    onSuccess: onChanged,
    onError,
  });

  return (
    <Card title="Аксессуары">
      <div className="space-y-3">
        {product.accessories.length === 0 ? (
          <p className="text-sm text-neutral-500">Не привязаны</p>
        ) : (
          <ul className="space-y-1.5">
            {product.accessories.map((link) => (
              <li key={link.accessoryId} className="flex items-center gap-3 text-sm">
                <span>{link.accessory.name.ru}</span>
                <span className="text-neutral-500">
                  {formatSum(link.priceOverride ?? link.accessory.price)}
                  {link.priceOverride !== null && <Badge>своя цена</Badge>}
                </span>
                <Button
                  variant="ghost"
                  className="ml-auto text-red-600"
                  onClick={() => unlink.mutate(link.accessoryId)}
                >
                  Отвязать
                </Button>
              </li>
            ))}
          </ul>
        )}

        {available.length > 0 && (
          <div className="flex gap-2 border-t border-neutral-200 pt-3 dark:border-neutral-800">
            <Select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="max-w-xs"
            >
              <option value="">Выберите аксессуар</option>
              {available.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name.ru} — {formatSum(a.price)}
                </option>
              ))}
            </Select>
            <Button disabled={!selected} onClick={() => link.mutate()}>
              Привязать
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
